import { addDoc, collection, deleteDoc, doc, getDocs, updateDoc } from 'firebase/firestore';
import {
  DEFAULT_COMPLIANCE_CATEGORIES,
  MANDATORY_COMPLIANCE_CATEGORY_IDS,
  type ComplianceCategory,
} from './complianceCategories';
import { sortDepartmentsByLofThenName } from './departmentUi';
import { db } from './firebase';
import { normalizeDepartmentId } from '../utils/normalizeIds';

type RefLike = { id?: string; path?: string };

const extractRefId = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'object') {
    const ref = value as RefLike;
    if (typeof ref.id === 'string' && ref.id.trim()) return ref.id.trim();
    if (typeof ref.path === 'string' && ref.path.trim()) {
      const parts = ref.path.split('/').filter(Boolean);
      return parts[parts.length - 1] ?? null;
    }
  }
  return null;
};

export interface Department {
  id: string;
  name: string;
  lofId: string;
  headWardNurseId?: string | null;
  compliances: string[];
}

const INCIDENT_IMPORT_ONLY_DEPARTMENT_IDS = new Set(['OPD_Primary', 'OPD_PC']);

const normalizeCompliances = (categories: ComplianceCategory[] | undefined | null): string[] => {
  const ids = (categories ?? [])
    .filter((category) => category && typeof category.id === 'string')
    .map((category) => category.id.trim())
    .filter(Boolean);

  return Array.from(new Set(ids));
};

const normalizeLegacyComplianceFields = (data: Record<string, unknown>): ComplianceCategory[] => {
  const record = data as Record<string, unknown>;

  if (Array.isArray(record.categories)) {
    return record.categories
      .filter((category) => category && typeof category === 'object')
      .map((category) => category as ComplianceCategory);
  }

  return [];
};

const coerceDepartmentName = (value: unknown, fallback: string): string => {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  const fromRef = extractRefId(value);
  if (fromRef) return fromRef;
  return fallback;
};

const normalizeDepartmentRecord = (
  id: string,
  data: Record<string, unknown>,
  fallbackName?: string,
): Department => {
  const lofId = (
    extractRefId((data as Record<string, unknown>).lofId) ??
    extractRefId((data as Record<string, unknown>).lof) ??
    extractRefId((data as Record<string, unknown>).lofRef) ??
    extractRefId((data as Record<string, unknown>).LOF) ??
    ''
  );

  const compliances = normalizeCompliances(
    Array.isArray(data.compliances)
      ? (data.compliances as string[]).map((c) => ({ id: c } as any))
      : normalizeLegacyComplianceFields(data),
  );

  return {
    id,
    name: coerceDepartmentName(data.name, fallbackName ?? id),
    lofId,
    headWardNurseId:
      extractRefId((data as Record<string, unknown>).headWardNurseId) ??
      extractRefId((data as Record<string, unknown>).HWN) ??
      null,
    compliances,
  };
};

export const getDepartments = async (): Promise<Department[]> => {
  const departmentsCollection = collection(db, 'departments');
  const snapshot = await getDocs(departmentsCollection);

  return sortDepartmentsByLofThenName(
    snapshot.docs.map((docSnap) =>
      normalizeDepartmentRecord(docSnap.id, docSnap.data() as Record<string, unknown>),
    ),
  );
};

export const getDepartmentsByLof = async (lofIds: string[]): Promise<Department[]> => {
  const allDepartments = await getDepartments();
  return sortDepartmentsByLofThenName(
    allDepartments.filter((department) => lofIds.includes(department.lofId)),
  );
};

export const addDepartment = async (department: Department) => {
  await addDoc(collection(db, 'departments'), department);
};

export const updateDepartment = async (id: string, department: Partial<Department>) => {
  const departmentRef = doc(db, 'departments', normalizeDepartmentId(id));
  await updateDoc(departmentRef, department as any);
};

export const deleteDepartment = async (id: string) => {
  const departmentRef = doc(db, 'departments', normalizeDepartmentId(id));
  await deleteDoc(departmentRef);
};

export const resolveScopedDepartments = (
  departments: Department[],
  scopedDepartmentIds: string[],
  lineOfWorkDepartmentIds: string[],
): Department[] => {
  const hasWildcard = scopedDepartmentIds.includes('*') || lineOfWorkDepartmentIds.includes('*');
  if (hasWildcard) return sortDepartmentsByLofThenName(departments);

  // When roleScope provides an explicit non-wildcard list (e.g. supervisor_nurse LOF), use ONLY that.
  // Do not merge with lineOfWorkDepartmentIds, which could contain the full NURSING_SERVICES map.
  const explicitScope = scopedDepartmentIds.filter((id) => id && id !== '*');
  if (explicitScope.length > 0) {
    const allowed = new Set(explicitScope);
    return sortDepartmentsByLofThenName(departments.filter((dept) => allowed.has(dept.id)));
  }

  const allowed = new Set<string>([...lineOfWorkDepartmentIds].filter(Boolean));
  return sortDepartmentsByLofThenName(departments.filter((dept) => allowed.has(dept.id)));
};

export const normalizeDepartmentCategories = (
  department: Department | undefined,
  allCategories: ComplianceCategory[],
): string[] => {
  const categoryMap = new Map(allCategories.map((cat) => [cat.id, cat]));
  const resolved =
    department?.compliances
      ?.map((id) => (typeof id === 'string' ? id.trim() : ''))
      .filter(Boolean)
      .map((id) => categoryMap.get(id)?.id ?? id) || [];

  const merged = new Set<string>([...resolved, ...MANDATORY_COMPLIANCE_CATEGORY_IDS]);

  if (!merged.size) return DEFAULT_COMPLIANCE_CATEGORIES.map((cat) => cat.id);
  return Array.from(merged);
};

export const normalizeDepartmentCategoryIds = (
  department: Department | undefined,
  allCategories: ComplianceCategory[],
): string[] => {
  const categoryMap = new Map(allCategories.map((cat) => [cat.id, cat]));
  return (
    department?.compliances
      ?.map((id) => categoryMap.get(id)?.id)
      .filter(Boolean) as string[]
  ) || [];
};

export const hasDepartmentCategoriesConfigured = (department: Department | undefined): boolean => {
  if (department?.id && INCIDENT_IMPORT_ONLY_DEPARTMENT_IDS.has(department.id)) return true;

  return !!department?.compliances?.some((id) => !!id && !MANDATORY_COMPLIANCE_CATEGORY_IDS.includes(id));
};

export const findDepartmentsMissingCompliances = (departments: Department[]) => {
  return departments.filter((dept) => !hasDepartmentCategoriesConfigured(dept));
};

export const resolveDepartmentCategoryObjects = (
  department: Department,
  allCategories: ComplianceCategory[],
): ComplianceCategory[] => {
  if (!department.compliances) return [];

  const categoryMap = new Map(allCategories.map((cat) => [cat.id, cat]));
  return department.compliances.map((id) => categoryMap.get(id)).filter(Boolean) as ComplianceCategory[];
};
